import { useState } from 'react';
import { programs } from '../../../mocks/programs';
import { useElementScrollAnimation } from '../../../hooks/useScrollAnimation';
import ProgramModal from './ProgramModal';

const FALLBACK_COLORS = [
  'from-red-900 to-neutral-900',
  'from-neutral-800 to-neutral-900',
  'from-zinc-800 to-neutral-900',
  'from-stone-800 to-neutral-900',
  'from-slate-800 to-neutral-900',
];

export default function ProgramsSection() {
  const [hovered, setHovered] = useState<number | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [imgErrors, setImgErrors] = useState<Record<number, boolean>>({});
  const containerRef = useElementScrollAnimation();
  const selectedProgram = programs.find((p) => p.id === selectedId) ?? null;

  const handleImgError = (id: number) => {
    setImgErrors((prev) => ({ ...prev, [id]: true }));
  };

  const renderCard = (program: typeof programs[0], i: number, delayClass: string) => (
    <div
      key={program.id}
      className={`animate-on-scroll rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 ${delayClass} bg-neutral-900 border border-neutral-800`}
      onMouseEnter={() => setHovered(program.id)}
      onMouseLeave={() => setHovered(null)}
    >
      {/* Image */}
      <div className="relative overflow-hidden h-64 w-full">
        {!imgErrors[program.id] ? (
          <img
            src={program.image}
            alt={program.title}
            className={`w-full h-full object-cover object-top transition-transform duration-700 ${
              hovered === program.id ? 'scale-110' : 'scale-100'
            }`}
            onError={() => handleImgError(program.id)}
          />
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${FALLBACK_COLORS[i % FALLBACK_COLORS.length]} flex items-center justify-center`}>
            <i className="ri-film-line text-white/20 text-6xl" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
        <div className="absolute top-4 left-4">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
            i === 0 ? 'bg-red-800 text-white' : 'bg-white/10 text-white border border-white/20'
          }`}>
            {program.label}
          </span>
        </div>
      </div>
      {/* Content */}
      <div className="p-6">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="font-serif-kr font-bold text-xl text-white">{program.title}</h3>
            <p className="text-xs mt-0.5 text-neutral-500">{program.subtitle}</p>
          </div>
        </div>
        <p className="text-sm leading-relaxed mt-3 mb-4 text-neutral-400">{program.description}</p>
        <ul className="space-y-1.5 mb-6">
          {program.details.map((detail, di) => (
            <li key={di} className="flex items-center gap-2 text-xs text-neutral-500">
              <span className="w-1 h-1 rounded-full bg-red-500 flex-shrink-0" />
              {detail}
            </li>
          ))}
        </ul>
        <button
          onClick={() => setSelectedId(program.id)}
          className="w-full py-2.5 rounded-full text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap bg-red-800 text-white hover:bg-red-700"
        >
          자세히 보기 <i className="ri-arrow-right-line ml-1" />
        </button>
      </div>
    </div>
  );

  return (
    <section id="programs" className="py-24 bg-black" ref={containerRef}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16 animate-on-scroll">
          <span className="text-red-500 text-xs font-semibold tracking-widest uppercase mb-4 block">Programs</span>
          <h2 className="font-serif-kr font-black text-white text-4xl md:text-5xl leading-tight">
            〔 찬연기아카데미 프로그램 〕
          </h2>
          <p className="text-neutral-400 text-sm mt-4 max-w-md mx-auto leading-relaxed">
            연령과 목적에 맞는 다양한 프로그램으로 여러분의 꿈을 지원합니다
          </p>
        </div>

        {/* Cards Grid - Row 1 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {programs.slice(0, 3).map((program, i) => {
            const delayClass = i === 0 ? 'animate-delay-100' : i === 1 ? 'animate-delay-200' : 'animate-delay-300';
            return renderCard(program, i, delayClass);
          })}
        </div>

        {/* Cards Grid - Row 2 (뮤지컬 + 현대무용) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {programs.slice(3).map((program, i) => {
            const delayClass = i === 0 ? 'animate-delay-400' : 'animate-delay-500';
            return renderCard(program, i + 3, delayClass);
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center animate-on-scroll animate-delay-400">
          <p className="text-neutral-500 text-sm mb-4">모든 프로그램은 <strong className="text-white">무료 체험 수업</strong>을 제공합니다</p>
          <button
            onClick={() => {
              const el = document.getElementById('contact');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-red-700 text-red-500 text-sm font-semibold hover:bg-red-800 hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-calendar-line" />
            체험 수업 예약하기
          </button>
        </div>
      </div>

      {/* Modal */}
      {selectedProgram && (
        <ProgramModal program={selectedProgram} onClose={() => setSelectedId(null)} />
      )}
    </section>
  );
}
